import { TOOL_SEARCH_DATA } from "../app/toolSearchData";

export const TOOL_SECTION_LABELS = {
  workmate: "워크메이트", classmate: "클래스메이트", informate: "인포메이트",
  timetable: "시간표", community: "소통공간", account: "계정",
};
export type SearchableTool = (typeof TOOL_SEARCH_DATA)[number];
export const TOOL_SEARCH_EXAMPLES = ["자리 바꾸기", "서명 받기", "PDF 합치기", "세특", "모둠 짜기", "시간표"];

function normalize(value: string): string {
  return value.normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
}

const INITIALS = "ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ";
function initials(value: string): string {
  return [...value].map(char => {
    const code = char.charCodeAt(0) - 0xac00;
    return code >= 0 && code <= 11171 ? INITIALS[Math.floor(code / 588)] : char;
  }).join("");
}

// Keep the index outside React renders; searching never sends typed text to a server.
const index = TOOL_SEARCH_DATA.map(tool => ({
  tool,
  name: normalize(tool.name),
  initials: normalize(initials(normalize(tool.name))),
  summary: normalize(tool.summary),
  section: normalize(TOOL_SECTION_LABELS[tool.section]),
  keywords: tool.keywords.map(normalize),
}));

// Strip conversational endings and particles only as an alternative to the original word.
function wordForms(word: string): string[] {
  const forms = [word];
  const stem = word.replace(/(?:하고싶어요|하고싶어|고싶어요|고싶어|해주세요|해줘|하려고|하려면|할래|하기|하고|해서|해요|줘요|주세요|고|기|을|를|이랑|이|가|은|는|로|으로)$/, "");
  if (stem.length >= 2) forms.push(stem);
  else if (["받", "짜", "재", "쓰"].includes(stem)) forms.push(`${stem}기`);
  return forms;
}

const filler = /^(?:내가|제가|나|저|좀|다시|우리|반|학생들|아이들|싶어|싶어요|싶은데|하고|싶다|하려고|하는|할|수|있는|도구|기능|찾기|찾아줘|찾고|만들고|만들기|만들어줘|만들어|하기|해주세요|해줘|주세요|하고싶어|하고싶어요)$/;

export function searchToolDirectory(query: string): SearchableTool[] {
  const full = normalize(query.trim());
  if (!full) return [];
  const words = [...new Set(query.toLowerCase().normalize("NFKC").split(/[^\p{L}\p{N}]+/u).filter(Boolean))];
  const meaningful = words.filter(word => !filler.test(word));
  const tokens = (meaningful.length ? meaningful : words).map(wordForms);
  const initialQuery = /^[ㄱ-ㅎ]{2,}$/.test(query.trim());

  return index.map(entry => {
    const exact = entry.name === full;
    const titleMatch = entry.name.includes(full);
    const aliasMatch = entry.keywords.some(keyword => keyword === full);
    const initialMatch = initialQuery && entry.initials.includes(full);
    const scores = tokens.map(forms => Math.max(...forms.map(word =>
      entry.name.includes(word) ? 40 :
      entry.keywords.some(keyword => keyword.includes(word)) ? 24 :
      entry.summary.includes(word) ? 16 :
      entry.section.includes(word) ? 8 : 0,
    )));
    const matches = exact || titleMatch || aliasMatch || initialMatch || scores.every(score => score > 0);
    const score = (exact ? 1000 : 0) + (titleMatch ? 200 : 0) + (aliasMatch ? 120 : 0) + (initialMatch ? 80 : 0) + scores.reduce((sum, value) => sum + value, 0);
    return { tool: entry.tool, score: matches ? score : 0 };
  }).filter(hit => hit.score > 0).sort((a, b) => b.score - a.score).map(hit => hit.tool);
}

export const SUGGESTED_TOOLS = [
  "/classmate/arrangements", "/workmate/student-record", "/workmate/file-converter",
  "/classmate/random-pick", "/classmate/timer", "/workmate/timetable",
].flatMap(route => TOOL_SEARCH_DATA.find(tool => tool.route === route) ?? []);

export const TOOL_SEARCH_SHORTCUT_KEY = "fe-tool-search-shortcut-v1";

/** Respect focused controls, editors, IME, other dialogs, and browser shortcuts. */
export function canOpenToolSearch(event: KeyboardEvent, doc: Document = document): boolean {
  if (event.defaultPrevented || event.repeat || event.isComposing || event.keyCode === 229 ||
    event.ctrlKey || event.metaKey || event.altKey || event.shiftKey ||
    !(event.code === "KeyF" || event.key.toLowerCase() === "f") || doc.fullscreenElement) return false;
  const controls = 'input, textarea, select, [role="textbox"], [role="combobox"], [role="searchbox"], [role="listbox"], [role="menu"], [role="grid"], [role="tree"], [role="application"], [data-tool-search-ignore]';
  const isEditing = (element: EventTarget | null) => element instanceof HTMLElement &&
    (element.isContentEditable || !!element.closest(controls));
  if (event.composedPath().some(isEditing) || isEditing(doc.activeElement)) return false;
  if (doc.getSelection()?.toString()) return false;
  return ![...doc.querySelectorAll('dialog[open], [role="dialog"], [role="alertdialog"], [aria-modal="true"]')]
    .some(element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden");
}
